import React, { useState } from 'react';
import { Layout } from '../components/Layout/Layout';
import { BarChartIcon, CalendarIcon, CheckCircleIcon, ClockIcon, CloudIcon, DropletIcon, LineChartIcon, Loader2Icon, MapPinIcon, SunIcon, ThermometerIcon, XCircleIcon, ChevronDownIcon, ChevronUpIcon, PlusIcon, TrashIcon, ArrowRightIcon, AlertCircleIcon, CameraIcon, UploadIcon } from 'lucide-react';
import { ProjectAnalyticsChart } from '../components/Charts/ProjectAnalyticsChart';
import { motion, AnimatePresence } from 'framer-motion';
export const ProgressUpdatePage = () => {
  const [selectedProject, setSelectedProject] = useState<number>(1);
  const [expandedCard, setExpandedCard] = useState<string | null>('overview');
  const [newTask, setNewTask] = useState<string>('');
  const [showAddUpdate, setShowAddUpdate] = useState(false);
  // Sample project data - Updated for Malaysia
  const projects = [{
    id: 1,
    title: 'Sustainable Rice Farming',
    location: 'MADA Region, Kedah',
    progress: 65,
    startDate: 'Mar 15, 2023',
    endDate: 'Mar 15, 2024',
    status: 'On Track',
    image: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1632&q=80'
  }, {
    id: 2,
    title: 'Vertical Farming Technology',
    location: 'Cyberjaya, Selangor',
    progress: 40,
    startDate: 'Jun 1, 2023',
    endDate: 'Dec 1, 2024',
    status: 'Delayed',
    image: 'https://images.unsplash.com/photo-1585150694882-7d716d1062a5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'
  }];
  // Sample milestones data - Updated for Malaysian rice farming
  const milestones = [{
    id: 1,
    title: 'Land Preparation',
    dueDate: 'Mar 30, 2023',
    status: 'completed',
    description: 'Complete soil testing, field leveling, and irrigation setup in MADA scheme',
    completedDate: 'Mar 28, 2023'
  }, {
    id: 2,
    title: 'Seedling Preparation',
    dueDate: 'Apr 15, 2023',
    status: 'completed',
    description: 'Plant rice seeds in nursery using water-efficient techniques',
    completedDate: 'Apr 12, 2023'
  }, {
    id: 3,
    title: 'Transplanting Phase',
    dueDate: 'May 10, 2023',
    status: 'completed',
    description: 'Transfer seedlings to paddy fields and monitor initial growth',
    completedDate: 'May 8, 2023'
  }, {
    id: 4,
    title: 'Mid-season Assessment',
    dueDate: 'Jul 15, 2023',
    status: 'in-progress',
    description: 'Evaluate crop health, water usage, and growth progress'
  }, {
    id: 5,
    title: 'Harvest Preparation',
    dueDate: 'Sep 30, 2023',
    status: 'upcoming',
    description: 'Prepare equipment and storage facilities for harvest'
  }, {
    id: 6,
    title: 'Harvest',
    dueDate: 'Oct 15, 2023',
    status: 'upcoming',
    description: 'Complete rice harvest and transport to processing facility'
  }];
  // Sample weather data - Updated for Malaysian tropical climate
  const weatherData = {
    current: {
      temp: 32,
      condition: 'Partly Cloudy',
      icon: <CloudIcon className="text-gray-500" data-id="element-994" />,
      humidity: 75,
      windSpeed: 12
    },
    forecast: [{
      day: 'Today',
      temp: 32,
      icon: <CloudIcon size={18} data-id="element-995" />
    }, {
      day: 'Thu',
      temp: 33,
      icon: <SunIcon size={18} data-id="element-996" />
    }, {
      day: 'Fri',
      temp: 34,
      icon: <SunIcon size={18} data-id="element-997" />
    }, {
      day: 'Sat',
      temp: 31,
      icon: <CloudIcon size={18} data-id="element-998" />
    }, {
      day: 'Sun',
      temp: 30,
      icon: <DropletIcon size={18} data-id="element-999" />
    }]
  };
  // Sample tasks data - Updated for Malaysian context
  const [tasks, setTasks] = useState([{
    id: 1,
    title: 'Check irrigation system',
    status: 'completed',
    dueDate: 'Sep 10, 2023'
  }, {
    id: 2,
    title: 'Apply organic fertilizer',
    status: 'completed',
    dueDate: 'Sep 12, 2023'
  }, {
    id: 3,
    title: 'Monitor water levels',
    status: 'in-progress',
    dueDate: 'Sep 15, 2023'
  }, {
    id: 4,
    title: 'Check for pest infestations',
    status: 'in-progress',
    dueDate: 'Sep 18, 2023'
  }, {
    id: 5,
    title: 'Prepare harvest equipment',
    status: 'upcoming',
    dueDate: 'Sep 25, 2023'
  }]);
  // Sample analytics data
  const growthData = [{
    name: 'Week 1',
    height: 5,
    soilHealth: 82,
    waterUsage: 100
  }, {
    name: 'Week 5',
    height: 12,
    soilHealth: 85,
    waterUsage: 95
  }, {
    name: 'Week 9',
    height: 18,
    soilHealth: 87,
    waterUsage: 85
  }, {
    name: 'Week 13',
    height: 22,
    soilHealth: 84,
    waterUsage: 80
  }, {
    name: 'Week 17',
    height: 24,
    soilHealth: 85,
    waterUsage: 75
  }, {
    name: 'Week 21',
    height: 24,
    soilHealth: 85,
    waterUsage: 70
  }];
  const yieldForecast = [{
    name: 'Current',
    value: 65
  }, {
    name: 'Projected',
    value: 35
  }];
  const resourceUsage = [{
    name: 'Jan',
    water: 100,
    fertilizer: 20,
    labor: 40
  }, {
    name: 'Feb',
    water: 95,
    fertilizer: 25,
    labor: 45
  }, {
    name: 'Mar',
    water: 90,
    fertilizer: 15,
    labor: 50
  }, {
    name: 'Apr',
    water: 85,
    fertilizer: 10,
    labor: 55
  }, {
    name: 'May',
    water: 80,
    fertilizer: 20,
    labor: 45
  }, {
    name: 'Jun',
    water: 75,
    fertilizer: 30,
    labor: 40
  }];
  // Sample updates data - Updated for Malaysian context
  const [updates, setUpdates] = useState([{
    id: 1,
    date: 'Sep 8, 2023',
    title: 'Mid-season progress',
    content: 'The paddy is growing well with healthy foliage. Water levels are maintained at optimal levels despite recent dry weather. No signs of pests or disease.',
    images: ['https://images.unsplash.com/photo-1530836369250-ef72a3f5cda8?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'],
    author: 'Ahmad Ismail'
  }, {
    id: 2,
    date: 'Aug 25, 2023',
    title: 'Irrigation system upgrade',
    content: 'Completed the installation of smart irrigation sensors across the paddy field. This should help reduce water usage by approximately 15%, which is critical during the current dry season.',
    images: [],
    author: 'Mei Ling Tan'
  }]);
  // Get selected project
  const activeProject = projects.find(p => p.id === selectedProject) || projects[0];
  // Toggle card expansion
  const toggleCard = (cardName: string) => {
    if (expandedCard === cardName) {
      setExpandedCard(null);
    } else {
      setExpandedCard(cardName);
    }
  };
  // Helper function for milestone status icon
  const getMilestoneIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon size={20} className="text-green-500" data-id="element-1000" />;
      case 'in-progress':
        return <Loader2Icon size={20} className="text-blue-500 animate-spin" data-id="element-1001" />;
      case 'delayed':
        return <XCircleIcon size={20} className="text-red-500" data-id="element-1002" />;
      default:
        return <ClockIcon size={20} className="text-gray-400" data-id="element-1003" />;
    }
  };
  // Helper function for task status
  const getTaskStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in-progress':
        return 'bg-blue-100 text-blue-800';
      case 'upcoming':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Add new task
  const addTask = () => {
    if (newTask.trim() !== '') {
      const newTaskItem = {
        id: tasks.length + 1,
        title: newTask,
        status: 'upcoming',
        dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        })
      };
      setTasks([...tasks, newTaskItem]);
      setNewTask('');
    }
  };
  // Toggle task status
  const toggleTaskStatus = (taskId: number) => {
    setTasks(tasks.map(task => task.id === taskId ? {
      ...task,
      status: task.status === 'completed' ? 'upcoming' : 'completed'
    } : task));
  };
  // Delete task
  const deleteTask = (taskId: number) => {
    setTasks(tasks.filter(task => task.id !== taskId));
  };
  return <Layout data-id="element-1004">
      <div className="container mx-auto px-4 py-8" data-id="element-1005">
        <div className="mb-8" data-id="element-1006">
          <h1 className="text-3xl font-bold text-soil-brown" data-id="element-1007">
            Project Progress
          </h1>
          <p className="text-gray-600 mt-1" data-id="element-1008">
            Track and monitor your agricultural projects
          </p>
        </div>
        {/* Project Selector */}
        <div className="flex overflow-x-auto space-x-4 mb-8 pb-2" data-id="element-1009">
          {projects.map(project => <motion.div key={project.id} whileHover={{
          y: -5
        }} className={`flex-shrink-0 w-72 rounded-lg overflow-hidden cursor-pointer transition-all ${selectedProject === project.id ? 'ring-2 ring-leaf-green shadow-md' : 'ring-1 ring-gray-200'}`} onClick={() => setSelectedProject(project.id)} data-id="element-1010">
              <div className="h-32 overflow-hidden" data-id="element-1011">
                <img src={project.image} alt={project.title} className="w-full h-full object-cover" data-id="element-1012" />
              </div>
              <div className="p-4" data-id="element-1013">
                <h3 className="font-semibold text-lg" data-id="element-1014">{project.title}</h3>
                <div className="flex items-center text-sm text-gray-600 mb-3" data-id="element-1015">
                  <MapPinIcon size={14} className="mr-1" data-id="element-1016" />
                  <span data-id="element-1017">{project.location}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2" data-id="element-1018">
                  <motion.div initial={{
                width: 0
              }} animate={{
                width: `${project.progress}%`
              }} transition={{
                duration: 1,
                ease: 'easeOut'
              }} className="bg-leaf-green h-2.5 rounded-full" data-id="element-1019"></motion.div>
                </div>
                <div className="flex justify-between text-sm" data-id="element-1020">
                  <span className="text-gray-600" data-id="element-1021">
                    {project.progress}% Complete
                  </span>
                  <span className={`font-medium ${project.status === 'On Track' ? 'text-green-600' : 'text-yellow-600'}`} data-id="element-1022">
                    {project.status}
                  </span>
                </div>
              </div>
            </motion.div>)}
          {/* Add New Project Button */}
          <div className="flex-shrink-0 w-72 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-leaf-green hover:bg-leaf-green/5 transition-colors" style={{
          height: '204px'
        }} data-id="element-1023">
            <div className="text-center p-4" data-id="element-1024">
              <div className="bg-gray-100 rounded-full p-3 inline-flex mb-2" data-id="element-1025">
                <PlusIcon size={24} className="text-gray-500" data-id="element-1026" />
              </div>
              <p className="font-medium text-gray-700" data-id="element-1027">Add New Project</p>
              <p className="text-sm text-gray-500 mt-1" data-id="element-1028">
                Track a new agricultural venture
              </p>
            </div>
          </div>
        </div>
        {/* Dashboard Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1029">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6" data-id="element-1030">
            {/* Overview Card */}
            <div className="card" data-id="element-1031">
              <div className="flex justify-between items-center mb-4 cursor-pointer" onClick={() => toggleCard('overview')} data-id="element-1032">
                <h2 className="text-xl font-semibold" data-id="element-1033">Project Overview</h2>
                <div className="flex items-center" data-id="element-1034">
                  <div className={`px-3 py-1 rounded-full text-sm font-medium mr-2 ${activeProject.status === 'On Track' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-1035">
                    {activeProject.status}
                  </div>
                  {expandedCard === 'overview' ? <ChevronUpIcon size={20} className="text-gray-500" data-id="element-1036" /> : <ChevronDownIcon size={20} className="text-gray-500" data-id="element-1037" />}
                </div>
              </div>
              <AnimatePresence initial={false} data-id="element-1038">
                {(expandedCard === 'overview' || expandedCard === null) && <motion.div initial="collapsed" animate="open" exit="collapsed" variants={{
                open: {
                  opacity: 1,
                  height: 'auto'
                },
                collapsed: {
                  opacity: 0,
                  height: 0
                }
              }} transition={{
                duration: 0.3
              }} data-id="element-1039">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-1040">
                      <div className="bg-gray-50 rounded-lg p-3" data-id="element-1041">
                        <p className="text-sm text-gray-500 mb-1" data-id="element-1042">Start Date</p>
                        <div className="flex items-center" data-id="element-1043">
                          <CalendarIcon size={16} className="mr-2 text-soil-brown" data-id="element-1044" />
                          <p className="font-medium" data-id="element-1045">
                            {activeProject.startDate}
                          </p>
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3" data-id="element-1046">
                        <p className="text-sm text-gray-500 mb-1" data-id="element-1047">End Date</p>
                        <div className="flex items-center" data-id="element-1048">
                          <CalendarIcon size={16} className="mr-2 text-soil-brown" data-id="element-1049" />
                          <p className="font-medium" data-id="element-1050">{activeProject.endDate}</p>
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3" data-id="element-1051">
                        <p className="text-sm text-gray-500 mb-1" data-id="element-1052">Completion</p>
                        <div className="flex items-center" data-id="element-1053">
                          <BarChartIcon size={16} className="mr-2 text-soil-brown" data-id="element-1054" />
                          <p className="font-medium" data-id="element-1055">
                            {activeProject.progress}% Complete
                          </p>
                        </div>
                      </div>
                    </div>
                    <div data-id="element-1056">
                      <h3 className="font-medium mb-3" data-id="element-1057">Progress Timeline</h3>
                      <div className="w-full bg-gray-100 rounded-full h-4 mb-4" data-id="element-1058">
                        <motion.div initial={{
                      width: 0
                    }} animate={{
                      width: `${activeProject.progress}%`
                    }} transition={{
                      duration: 1,
                      ease: 'easeOut'
                    }} className="bg-leaf-green h-4 rounded-full relative" data-id="element-1059">
                          <span className="absolute -right-4 top-6 text-sm font-medium text-leaf-green" data-id="element-1060">
                            Now
                          </span>
                        </motion.div>
                      </div>
                      <div className="flex justify-between text-sm text-gray-600" data-id="element-1061">
                        <span data-id="element-1062">{activeProject.startDate}</span>
                        <span data-id="element-1063">{activeProject.endDate}</span>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
            {/* Analytics Card */}
            <div className="card" data-id="element-1064">
              <div className="flex justify-between items-center mb-4 cursor-pointer" onClick={() => toggleCard('analytics')} data-id="element-1065">
                <h2 className="text-xl font-semibold" data-id="element-1066">Analytics & Insights</h2>
                <div className="flex items-center" data-id="element-1067">
                  {expandedCard === 'analytics' ? <ChevronUpIcon size={20} className="text-gray-500" data-id="element-1068" /> : <ChevronDownIcon size={20} className="text-gray-500" data-id="element-1069" />}
                </div>
              </div>
              <AnimatePresence initial={false} data-id="element-1070">
                {expandedCard === 'analytics' && <motion.div initial="collapsed" animate="open" exit="collapsed" variants={{
                open: {
                  opacity: 1,
                  height: 'auto'
                },
                collapsed: {
                  opacity: 0,
                  height: 0
                }
              }} transition={{
                duration: 0.3
              }} data-id="element-1071">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1072">
                      <div data-id="element-1073">
                        <ProjectAnalyticsChart title="Growth Metrics" data={growthData} type="line" dataKeys={['height', 'soilHealth', 'waterUsage']} xAxisLabel="Time" yAxisLabel="Value" data-id="element-1074" />
                      </div>
                      <div data-id="element-1075">
                        <ProjectAnalyticsChart title="Resource Usage" data={resourceUsage} type="bar" dataKeys={['water', 'fertilizer', 'labor']} xAxisLabel="Month" yAxisLabel="Units" data-id="element-1076" />
                      </div>
                      <div data-id="element-1077">
                        <ProjectAnalyticsChart title="Yield Forecast" data={yieldForecast} type="pie" dataKeys={['value']} data-id="element-1078" />
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1079">
                        <h3 className="font-medium mb-3" data-id="element-1080">Key Insights</h3>
                        <ul className="space-y-2" data-id="element-1081">
                          <li className="flex items-start" data-id="element-1082">
                            <div className="bg-green-100 p-1 rounded-full mr-2 mt-0.5" data-id="element-1083">
                              <ArrowRightIcon size={12} className="text-green-600" data-id="element-1084" />
                            </div>
                            <span className="text-sm" data-id="element-1085">
                              Water usage reduced by 30% compared to previous
                              monsoon season
                            </span>
                          </li>
                          <li className="flex items-start" data-id="element-1086">
                            <div className="bg-green-100 p-1 rounded-full mr-2 mt-0.5" data-id="element-1087">
                              <ArrowRightIcon size={12} className="text-green-600" data-id="element-1088" />
                            </div>
                            <span className="text-sm" data-id="element-1089">
                              Soil health has improved by 5% since project start
                            </span>
                          </li>
                          <li className="flex items-start" data-id="element-1090">
                            <div className="bg-yellow-100 p-1 rounded-full mr-2 mt-0.5" data-id="element-1091">
                              <AlertCircleIcon size={12} className="text-yellow-600" data-id="element-1092" />
                            </div>
                            <span className="text-sm" data-id="element-1093">
                              Projected yield may be affected by recent drought
                              conditions
                            </span>
                          </li>
                          <li className="flex items-start" data-id="element-1094">
                            <div className="bg-green-100 p-1 rounded-full mr-2 mt-0.5" data-id="element-1095">
                              <ArrowRightIcon size={12} className="text-green-600" data-id="element-1096" />
                            </div>
                            <span className="text-sm" data-id="element-1097">
                              Labor efficiency improved by 15% with new
                              transplanting techniques
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
            {/* Updates Card */}
            <div className="card" data-id="element-1098">
              <div className="flex justify-between items-center mb-4 cursor-pointer" onClick={() => toggleCard('updates')} data-id="element-1099">
                <h2 className="text-xl font-semibold" data-id="element-1100">Progress Updates</h2>
                <div className="flex items-center" data-id="element-1101">
                  <button className="btn btn-outline py-1 px-3 text-sm mr-2" onClick={e => {
                  e.stopPropagation();
                  setShowAddUpdate(!showAddUpdate);
                }} data-id="element-1102">
                    Add Update
                  </button>
                  {expandedCard === 'updates' ? <ChevronUpIcon size={20} className="text-gray-500" data-id="element-1103" /> : <ChevronDownIcon size={20} className="text-gray-500" data-id="element-1104" />}
                </div>
              </div>
              <AnimatePresence initial={false} data-id="element-1105">
                {showAddUpdate && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.3
              }} className="mb-6 border p-4 rounded-lg" data-id="element-1106">
                    <h3 className="font-medium mb-3" data-id="element-1107">Add Progress Update</h3>
                    <div className="space-y-4" data-id="element-1108">
                      <div data-id="element-1109">
                        <label className="block text-gray-700 font-medium mb-2" data-id="element-1110">
                          Title
                        </label>
                        <input type="text" className="input-field" placeholder="Enter update title" data-id="element-1111" />
                      </div>
                      <div data-id="element-1112">
                        <label className="block text-gray-700 font-medium mb-2" data-id="element-1113">
                          Description
                        </label>
                        <textarea className="input-field min-h-[100px]" placeholder="Describe the current progress, challenges, and achievements..." data-id="element-1114"></textarea>
                      </div>
                      <div data-id="element-1115">
                        <label className="block text-gray-700 font-medium mb-2" data-id="element-1116">
                          Add Photos
                        </label>
                        <div className="flex items-center space-x-4" data-id="element-1117">
                          <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-leaf-green hover:bg-leaf-green/5" data-id="element-1118">
                            <div className="text-center" data-id="element-1119">
                              <CameraIcon size={24} className="mx-auto text-gray-400" data-id="element-1120" />
                              <span className="text-xs text-gray-500 mt-1" data-id="element-1121">
                                Take Photo
                              </span>
                            </div>
                          </div>
                          <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-leaf-green hover:bg-leaf-green/5" data-id="element-1122">
                            <div className="text-center" data-id="element-1123">
                              <UploadIcon size={24} className="mx-auto text-gray-400" data-id="element-1124" />
                              <span className="text-xs text-gray-500 mt-1" data-id="element-1125">
                                Upload
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-end space-x-3" data-id="element-1126">
                        <button className="btn border border-gray-300 bg-white text-gray-700 hover:bg-gray-50" onClick={() => setShowAddUpdate(false)} data-id="element-1127">
                          Cancel
                        </button>
                        <button className="btn btn-primary" data-id="element-1128">Post Update</button>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
              <AnimatePresence initial={false} data-id="element-1129">
                {expandedCard === 'updates' && <motion.div initial="collapsed" animate="open" exit="collapsed" variants={{
                open: {
                  opacity: 1,
                  height: 'auto'
                },
                collapsed: {
                  opacity: 0,
                  height: 0
                }
              }} transition={{
                duration: 0.3
              }} data-id="element-1130">
                    <div className="space-y-6" data-id="element-1131">
                      {updates.map(update => <div key={update.id} className="border-b pb-6 last:border-0" data-id="element-1132">
                          <div className="flex justify-between items-start mb-2" data-id="element-1133">
                            <h3 className="font-medium text-lg" data-id="element-1134">
                              {update.title}
                            </h3>
                            <span className="text-sm text-gray-500" data-id="element-1135">
                              {update.date}
                            </span>
                          </div>
                          <p className="text-gray-700 mb-3" data-id="element-1136">{update.content}</p>
                          {update.images.length > 0 && <div className="flex space-x-2 mb-3 overflow-x-auto pb-2" data-id="element-1137">
                              {update.images.map((image, index) => <div key={index} className="flex-shrink-0 w-32 h-32" data-id="element-1138">
                                  <img src={image} alt={`Update ${update.id} image ${index}`} className="w-full h-full object-cover rounded-lg" data-id="element-1139" />
                                </div>)}
                            </div>}
                          <div className="flex items-center text-sm text-gray-500" data-id="element-1140">
                            <span data-id="element-1141">Posted by {update.author}</span>
                          </div>
                        </div>)}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
            {/* Milestones Card */}
            <div className="card" data-id="element-1142">
              <div className="flex justify-between items-center mb-4 cursor-pointer" onClick={() => toggleCard('milestones')} data-id="element-1143">
                <h2 className="text-xl font-semibold" data-id="element-1144">Project Milestones</h2>
                <div className="flex items-center" data-id="element-1145">
                  <button className="btn btn-outline py-1 px-3 text-sm mr-2" onClick={e => e.stopPropagation()} data-id="element-1146">
                    Add Milestone
                  </button>
                  {expandedCard === 'milestones' ? <ChevronUpIcon size={20} className="text-gray-500" data-id="element-1147" /> : <ChevronDownIcon size={20} className="text-gray-500" data-id="element-1148" />}
                </div>
              </div>
              <AnimatePresence initial={false} data-id="element-1149">
                {expandedCard === 'milestones' && <motion.div initial="collapsed" animate="open" exit="collapsed" variants={{
                open: {
                  opacity: 1,
                  height: 'auto'
                },
                collapsed: {
                  opacity: 0,
                  height: 0
                }
              }} transition={{
                duration: 0.3
              }} data-id="element-1150">
                    <div className="space-y-4" data-id="element-1151">
                      {milestones.map((milestone, index) => <div key={milestone.id} className="relative" data-id="element-1152">
                          {index < milestones.length - 1 && <div className={`absolute left-[10px] top-6 w-0.5 h-full -z-10 ${milestone.status === 'completed' ? 'bg-green-300' : 'bg-gray-200'}`} data-id="element-1153"></div>}
                          <motion.div className="flex" initial={{
                      opacity: 0,
                      x: -20
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      duration: 0.3,
                      delay: index * 0.1
                    }} data-id="element-1154">
                            <div className="flex-shrink-0 mt-1" data-id="element-1155">
                              {getMilestoneIcon(milestone.status)}
                            </div>
                            <div className="ml-4" data-id="element-1156">
                              <div className="flex flex-wrap items-baseline" data-id="element-1157">
                                <h3 className="text-lg font-medium" data-id="element-1158">
                                  {milestone.title}
                                </h3>
                                <span className={`ml-2 text-sm ${milestone.status === 'completed' ? 'text-green-600' : 'text-gray-500'}`} data-id="element-1159">
                                  {milestone.status === 'completed' ? `Completed on ${milestone.completedDate}` : `Due ${milestone.dueDate}`}
                                </span>
                              </div>
                              <p className="text-gray-600 mt-1" data-id="element-1160">
                                {milestone.description}
                              </p>
                              {milestone.status === 'in-progress' && <div className="mt-2 flex" data-id="element-1161">
                                  <button className="text-sm text-leaf-green hover:underline flex items-center" data-id="element-1162">
                                    <CheckCircleIcon size={14} className="mr-1" data-id="element-1163" />
                                    Mark as Complete
                                  </button>
                                </div>}
                            </div>
                          </motion.div>
                        </div>)}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>
          {/* Right Column */}
          <div className="space-y-6" data-id="element-1164">
            {/* Weather Card - Updated for Malaysian weather */}
            <div className="card" data-id="element-1165">
              <h2 className="text-xl font-semibold mb-4" data-id="element-1166">Local Weather</h2>
              <div className="flex items-center justify-between mb-4" data-id="element-1167">
                <div data-id="element-1168">
                  <div className="flex items-center" data-id="element-1169">
                    {weatherData.current.icon}
                    <span className="text-3xl font-bold ml-2" data-id="element-1170">
                      {weatherData.current.temp}°C
                    </span>
                  </div>
                  <p className="text-gray-600" data-id="element-1171">
                    {weatherData.current.condition}
                  </p>
                </div>
                <div data-id="element-1172">
                  <div className="flex items-center text-sm text-gray-600" data-id="element-1173">
                    <DropletIcon size={14} className="mr-1" data-id="element-1174" />
                    <span data-id="element-1175">Humidity: {weatherData.current.humidity}%</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600 mt-1" data-id="element-1176">
                    <ThermometerIcon size={14} className="mr-1" data-id="element-1177" />
                    <span data-id="element-1178">Wind: {weatherData.current.windSpeed} km/h</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-5 gap-2 border-t pt-3" data-id="element-1179">
                {weatherData.forecast.map((day, index) => <div key={index} className="text-center" data-id="element-1180">
                    <p className="text-xs text-gray-500" data-id="element-1181">{day.day}</p>
                    <div className="my-1" data-id="element-1182">{day.icon}</div>
                    <p className="font-medium" data-id="element-1183">{day.temp}°</p>
                  </div>)}
              </div>
              <button className="btn btn-outline w-full mt-4 text-sm" data-id="element-1184">
                View 10-Day Forecast
              </button>
            </div>
            {/* Tasks Card */}
            <div className="card" data-id="element-1185">
              <div className="flex justify-between items-center mb-4" data-id="element-1186">
                <h2 className="text-xl font-semibold" data-id="element-1187">Upcoming Tasks</h2>
                <button className="btn btn-outline py-1 px-3 text-sm" data-id="element-1188">
                  View All
                </button>
              </div>
              <div className="mb-4" data-id="element-1189">
                <div className="flex" data-id="element-1190">
                  <input type="text" className="input-field rounded-r-none" placeholder="Add a new task..." value={newTask} onChange={e => setNewTask(e.target.value)} onKeyPress={e => e.key === 'Enter' && addTask()} data-id="element-1191" />
                  <button className="btn btn-primary rounded-l-none" onClick={addTask} data-id="element-1192">
                    Add
                  </button>
                </div>
              </div>
              <div className="space-y-2 max-h-80 overflow-y-auto" data-id="element-1193">
                <AnimatePresence initial={false} data-id="element-1194">
                  {tasks.map(task => <motion.div key={task.id} className="flex items-center p-2 hover:bg-gray-50 rounded-md group" initial={{
                  opacity: 0,
                  y: -10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.2
                }} data-id="element-1195">
                      <input type="checkbox" id={`task-${task.id}`} checked={task.status === 'completed'} className="h-4 w-4 text-leaf-green rounded focus:ring-leaf-green" onChange={() => toggleTaskStatus(task.id)} data-id="element-1196" />
                      <label htmlFor={`task-${task.id}`} className={`ml-3 flex-1 ${task.status === 'completed' ? 'line-through text-gray-400' : ''}`} data-id="element-1197">
                        {task.title}
                      </label>
                      <span className={`text-xs px-2 py-1 rounded-full ${getTaskStatusColor(task.status)}`} data-id="element-1198">
                        {task.status === 'upcoming' ? task.dueDate : task.status}
                      </span>
                      <button className="ml-2 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => deleteTask(task.id)} data-id="element-1199">
                        <TrashIcon size={16} data-id="element-1200" />
                      </button>
                    </motion.div>)}
                </AnimatePresence>
              </div>
            </div>
            {/* Growth Stats Card - Updated for paddy farming */}
            <div className="card" data-id="element-1201">
              <div className="flex justify-between items-center mb-4" data-id="element-1202">
                <h2 className="text-xl font-semibold" data-id="element-1203">Growth Statistics</h2>
                <div className="flex items-center text-sm text-gray-600" data-id="element-1204">
                  <LineChartIcon size={16} className="mr-1" data-id="element-1205" />
                  <span data-id="element-1206">Last updated: Today</span>
                </div>
              </div>
              <div className="space-y-4" data-id="element-1207">
                <div data-id="element-1208">
                  <div className="flex justify-between mb-1" data-id="element-1209">
                    <span className="text-sm text-gray-600" data-id="element-1210">Paddy Height</span>
                    <span className="text-sm font-medium" data-id="element-1211">60 cm</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-1212">
                    <motion.div initial={{
                    width: 0
                  }} animate={{
                    width: '70%'
                  }} transition={{
                    duration: 1,
                    ease: 'easeOut'
                  }} className="bg-leaf-green h-2 rounded-full" data-id="element-1213"></motion.div>
                  </div>
                </div>
                <div data-id="element-1214">
                  <div className="flex justify-between mb-1" data-id="element-1215">
                    <span className="text-sm text-gray-600" data-id="element-1216">Water Usage</span>
                    <span className="text-sm font-medium" data-id="element-1217">65% of plan</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-1218">
                    <motion.div initial={{
                    width: 0
                  }} animate={{
                    width: '65%'
                  }} transition={{
                    duration: 1,
                    ease: 'easeOut',
                    delay: 0.2
                  }} className="bg-sky-blue h-2 rounded-full" data-id="element-1219"></motion.div>
                  </div>
                </div>
                <div data-id="element-1220">
                  <div className="flex justify-between mb-1" data-id="element-1221">
                    <span className="text-sm text-gray-600" data-id="element-1222">Soil Health</span>
                    <span className="text-sm font-medium" data-id="element-1223">85% optimal</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-1224">
                    <motion.div initial={{
                    width: 0
                  }} animate={{
                    width: '85%'
                  }} transition={{
                    duration: 1,
                    ease: 'easeOut',
                    delay: 0.4
                  }} className="bg-soil-brown h-2 rounded-full" data-id="element-1225"></motion.div>
                  </div>
                </div>
                <button className="btn btn-outline w-full mt-2 text-sm" data-id="element-1226">
                  View Detailed Reports
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>;
};